/** App-wide UI state: which sheet is open, the date being viewed, and toasts. */
import { lazy, Suspense, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ISODate, Meal } from '../../shared/dates';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Sheet } from '../components/Sheet';
import { useToday } from './today';
import { UIContext, type LogOptions, type Toast, type UIContextValue } from './uiContext';

const LogSheet = lazy(() => import('../components/log/LogSheet'));
const WeighInSheet = lazy(() => import('../components/WeighInSheet'));
const WhatFitsSheet = lazy(() => import('../components/WhatFitsSheet'));

export type { LogMode, LogOptions, LogTab } from './uiContext';
export { useUI } from './uiContext';

export function UIProvider({ children }: { children: ReactNode }) {
  const today = useToday();
  const [selected, setSelected] = useState<ISODate | null>(null);
  const selectedDate = selected ?? today;
  const [log, setLog] = useState<(LogOptions & { key: number }) | null>(null);
  const [weighIn, setWeighIn] = useState<{ date?: ISODate; key: number } | null>(null);
  const [fits, setFits] = useState<{ meal?: Meal; date?: ISODate; key: number } | null>(null);
  const closeSheets = () => {
    setLog(null);
    setWeighIn(null);
    setFits(null);
  };
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const toast = useCallback<UIContextValue['toast']>((message, opts) => {
    const id = ++seq.current;
    setToasts((list) => [...list.slice(-2), { id, message, tone: opts?.tone ?? 'default', action: opts?.action }]);
    window.setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), opts?.action ? 6000 : 3800);
  }, []);

  const value = useMemo<UIContextValue>(
    () => ({
      today,
      selectedDate,
      setSelectedDate: (d) => setSelected(d === today ? null : d),
      openLog: (opts) => {
        setFits(null);
        setLog({ ...opts, key: ++seq.current });
      },
      openWeighIn: (opts) => setWeighIn({ ...opts, key: ++seq.current }),
      openWhatFits: (opts) => setFits({ ...opts, key: ++seq.current }),
      toast,
    }),
    [today, selectedDate, toast],
  );

  return (
    <UIContext.Provider value={value}>
      {children}
      {/* A crashing sheet (or a chunk that fails to load after a rebuild) must not take the page down. */}
      <ErrorBoundary
        resetKey={`${log?.key ?? 0}:${weighIn?.key ?? 0}:${fits?.key ?? 0}`}
        fallback={() => (
          <Sheet title="Something went wrong" size="sm" onClose={closeSheets}>
            <div className="stack">
              <p className="small muted">This panel couldn't open. Everything you've logged is saved.</p>
              <div className="row">
                <button className="btn btn--primary" onClick={() => window.location.reload()}>
                  Reload
                </button>
                <button className="btn btn--ghost" onClick={closeSheets}>
                  Close
                </button>
              </div>
            </div>
          </Sheet>
        )}
      >
        <Suspense fallback={null}>
          {log && <LogSheet key={log.key} options={log} onClose={() => setLog(null)} />}
          {weighIn && <WeighInSheet key={weighIn.key} date={weighIn.date} onClose={() => setWeighIn(null)} />}
          {fits && <WhatFitsSheet key={fits.key} meal={fits.meal} date={fits.date} onClose={() => setFits(null)} />}
        </Suspense>
      </ErrorBoundary>
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone === 'error' ? 'toast--error' : ''}`}>
            <span className="grow">{t.message}</span>
            {t.action && (
              <button
                className="btn btn--sm"
                onClick={() => {
                  t.action?.onClick();
                  setToasts((list) => list.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </UIContext.Provider>
  );
}
